import {Box, Button, IconButton, TextField, Typography} from "@mui/material";
import {Add as AddIcon, DeleteOutline as DeleteIcon} from "@mui/icons-material";
import {useState} from "react";
import {useNutritionDisplayMode} from "../../contexts/NutritionDisplayContext";
import {POINTS, energyFromInput, energyToInput, macroFromInput, macroToInput} from "../nutrition/v2/ui/nutritionUnits";

const sectionLabelSx = {
    fontSize: "14px",
    fontWeight: 600,
    color: "#4F535C",
    marginBottom: "8px",
    textAlign: "right"
};

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#F3F3F5',
        '&:hover fieldset': {borderColor: '#57837E'},
        '&.Mui-focused fieldset': {borderColor: '#57837E'}
    }
};

const addButtonSx = {
    color: "#57837E",
    borderColor: "#E2E8F0",
    backgroundColor: "#FFFFFF",
    textTransform: "none",
    width: "100%",
    justifyContent: "center",
    "&:hover": {borderColor: "#57837E", backgroundColor: "#F8FAFC"}
};

/**
 * Recipe nutrition editor: measurement units — each with its own name/amount/calories
 * and the four core macros. Fully controlled by the parent.
 *
 * Points mode (the company counts in נקודות): calories and every macro with a `pointsKey` are
 * SHOWN and TYPED in points, while `units` keeps holding kcal and grams — the parent saves what
 * it always saved. Fibre and the unit's amount stay as they are.
 */
const RecipeNutritionFields = ({
    labels,
    macrosConfig = [],
    disabled = false,
    units = [],
    onUnitsChange,
    required = false,
}) => {
    // A recipe must have at least one serving unit, and every field on each unit is
    // required (see PA-54).
    const isFieldFilled = (v) => v !== "" && v !== null && v !== undefined && String(v).trim().length > 0;
    const isUnitComplete = (u) =>
        isFieldFilled(u.name) &&
        isFieldFilled(u.amount) &&
        isFieldFilled(u.calories) &&
        macrosConfig.every((m) => m.optional || isFieldFilled(u[m.key]));
    const allUnitsComplete = units.length > 0 && units.every(isUnitComplete);
    const showRequiredError = required && !allUnitsComplete;

    const inPoints = useNutritionDisplayMode() === POINTS;
    // Points mode only: the label set a points company reads, falling back to the regular one.
    const label = (key) => (inPoints && labels.nutritionPoints?.[key]) || labels[key];
    /*
     * The field being typed in, exactly as typed ("1." included). Its value is re-derived from
     * the stored grams otherwise, which would eat a trailing point mid-keystroke. Cleared on blur.
     */
    const [draft, setDraft] = useState(null);
    // kcal / grams ↔ the points a field shows. `pointsKey` null = not converted (fibre, amounts).
    const toShown = (stored, pointsKey) => {
        if (!inPoints || !pointsKey || !isFieldFilled(stored)) return stored ?? "";
        const n = Number(stored);
        if (Number.isNaN(n)) return stored;
        return String(pointsKey === "energy" ? energyToInput(n, POINTS) : macroToInput(pointsKey, n, POINTS));
    };
    const fromShown = (typed, pointsKey) => {
        if (!inPoints || !pointsKey || !isFieldFilled(typed)) return typed;
        const n = Number(typed);
        if (Number.isNaN(n)) return typed;
        return String(pointsKey === "energy" ? energyFromInput(n, POINTS) : macroFromInput(pointsKey, n, POINTS));
    };
    /** The props a converted number field needs: what it shows, and how a keystroke is stored. */
    const convertedField = (index, key, pointsKey) => {
        const id = `${index}:${key}`;
        return {
            value: draft && draft.id === id ? draft.value : toShown(units[index][key], pointsKey),
            onChange: (e) => {
                if (inPoints && pointsKey) setDraft({id, value: e.target.value});
                updateUnit(index, key, fromShown(e.target.value, pointsKey));
            },
            onBlur: () => setDraft(null),
        };
    };
    const emptyUnit = () => {
        const unit = {name: "", amount: "", calories: ""};
        macrosConfig.forEach((macro) => {
            unit[macro.key] = "";
        });
        return unit;
    };

    const updateUnit = (index, key, value) => {
        onUnitsChange(units.map((u, i) => (i === index ? {...u, [key]: value} : u)));
    };
    const addUnit = () => onUnitsChange([...units, emptyUnit()]);
    const removeUnit = (index) => onUnitsChange(units.filter((_, i) => i !== index));

    return (
        <Box>
            <Typography sx={{...sectionLabelSx, marginBottom: "4px"}}>
                {label("nutritionTitle")}
                {required && <span style={{color: "#F16063", marginInlineStart: "4px"}}>*</span>}
            </Typography>
            <Typography sx={{fontSize: "12px", color: "#94A3B8", marginBottom: showRequiredError ? "4px" : "12px", textAlign: "right"}}>
                {label("nutritionSubtitle")}
            </Typography>
            {showRequiredError && (
                <Typography sx={{fontSize: "12px", color: "#F16063", marginBottom: "12px", textAlign: "right"}}>
                    {label("nutritionRequiredError") || "יש למלא את כל השדות בכל יחידת מידה"}
                </Typography>
            )}

            {/* Measurement units — each with its own calories + macros */}
            <Box sx={{display: "flex", flexDirection: "column", gap: "12px"}}>
                {units.map((unit, index) => (
                    <Box
                        key={index}
                        sx={{
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                            padding: "12px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px"
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                            <TextField
                                sx={{...fieldSx, flex: 2}}
                                placeholder={labels.unitNamePlaceholder}
                                value={unit.name ?? ""}
                                onChange={(e) => updateUnit(index, "name", e.target.value)}
                                disabled={disabled}
                            />
                            <TextField
                                sx={{...fieldSx, flex: 1}}
                                type="number"
                                placeholder={labels.unitAmountPlaceholder}
                                value={unit.amount ?? ""}
                                onChange={(e) => updateUnit(index, "amount", e.target.value)}
                                disabled={disabled}
                            />
                            <TextField
                                sx={{...fieldSx, flex: 1}}
                                type="number"
                                placeholder={label("unitCaloriesPlaceholder")}
                                {...convertedField(index, "calories", "energy")}
                                disabled={disabled}
                            />
                            {!disabled && (
                                <IconButton size="small" onClick={() => removeUnit(index)} aria-label="remove unit">
                                    <DeleteIcon fontSize="small" sx={{color: "#94A3B8"}}/>
                                </IconButton>
                            )}
                        </Box>

                        {macrosConfig.length > 0 && (
                            <Box sx={{display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px"}}>
                                {macrosConfig.map((macro) => (
                                    <Box key={macro.key}>
                                        <Typography sx={{fontSize: "12px", color: "#94A3B8", marginBottom: "4px", textAlign: "right"}}>
                                            {inPoints && macro.pointsKey ? macro.pointsLabel : macro.label}
                                            {macro.optional && (
                                                <span style={{color: "#B8C0CC"}}> (אופציונלי)</span>
                                            )}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            sx={fieldSx}
                                            type="number"
                                            placeholder="0"
                                            {...convertedField(index, macro.key, macro.pointsKey)}
                                            disabled={disabled}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>

            {!disabled && (
                <Button
                    variant="outlined"
                    startIcon={<AddIcon/>}
                    onClick={addUnit}
                    sx={{...addButtonSx, marginTop: units.length ? "12px" : 0}}
                >
                    {labels.addUnitButton}
                </Button>
            )}
        </Box>
    );
};

export default RecipeNutritionFields;
